import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import ExpressionsControl from '../../shared/config/ExpressionsControl';
import { IPackage } from '../../../models/configuration/plugin/IConfiguration';
import { getUpdatedPersonas } from '../../../utils/personaHelper';
import { usePersonas } from '../../../hooks/usePersonas';
import DtTabs from '../../ui/tab/DtTabs';
import DtList from '../../ui/list/DtList';
import {  AccountCircle } from '@mui/icons-material';
import { EmptyState } from '../../ui/emptyState';
import { DtCard } from '../../ui/card';
import { DtDialog, DtConfirmDialog } from '../../ui/dialog';
import DtForm from '../../ui/form/DtForm';

interface packagesTabProps {
    tabIndex: number,
    selectedPackage: IPackage | null;
    updateParentPackage: (updatedPackage: IPackage) => void;
    sendUserUpdate: (users: string[]) => void;
    children?: React.ReactNode
}

const PackagesTab: React.FC<packagesTabProps> = ({
    selectedPackage,
    updateParentPackage,
    sendUserUpdate
}) => {
    const [tabValue, setTabValue] = React.useState(0);
    const [selectedRoles, setSelectedRoles] = useState<string[]>(selectedPackage?.Roles ?? []);
    const [users, setUsers] = useState<string[]>(selectedPackage?.Users ?? []);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const addUserFormRef = React.useRef<HTMLFormElement>(null);

    const personas = usePersonas();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        setSelectedRoles(selectedPackage?.Roles ?? []);
        setUsers(selectedPackage?.Users ?? []);
    }, [selectedPackage]);

    const onPersonaSelected = (item: string) => {
        const updatedRoles = getUpdatedPersonas(selectedRoles, item);
        setSelectedRoles(updatedRoles);
        if (selectedPackage) {
            updateParentPackage({ ...selectedPackage, Roles: updatedRoles });
        }
    };

    const handleAddUserDialogOpen = () => setAddUserDialogOpen(true);
    const handleAddUserDialogClosed = () => setAddUserDialogOpen(false);
    const handleAddUserValue = (value: string) => {
        if (!value || value.trim() === "") {
            return "Username is required";
        }
        
        const trimmedValue = value.trim();
        if (users.includes(trimmedValue)) {
            return "Username already exists";
        }
        else {
            const updatedUserList = [...users, trimmedValue];
            setUsers(updatedUserList);
            sendUserUpdate(updatedUserList);
            return "";
        }
    };

    const handleRemoveUserDialogOpen = () => setRemoveUserDialogOpen(true);
    const handleRemoveUserDialogClosed = () => setRemoveUserDialogOpen(false);
    const handleRemoveUserValue = (user: string | null) => {
        if (user) {
            const updatedUsers = users.filter(u => u !== user);
            setUsers(updatedUsers);
            sendUserUpdate(updatedUsers);
            setRemoveUserDialogOpen(false);
        }
    };

    const updateExpressions = (expressions: any) => {
        if (selectedPackage) {
            updateParentPackage({ ...selectedPackage, Expressions: expressions });
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <DtTabs
                tabs={[
                    { label: "Security" },
                    { label: "Expressions" }
                ]}
                value={tabValue}
                onChange={handleTabChange}
            >
                {/* Security Tab */}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DtCard header="Choose Personas with Access">
                            <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: { md: '34vh', lg: '42vh', xl: '56vh' } }}>
                                <DtList
                                    items={personas.map(p => ({
                                        id: p,
                                        label: p,
                                        checked: selectedRoles.includes(p),
                                    }))}
                                    selectable
                                    onSelect={(id, checked) => onPersonaSelected(id as string)}
                                />
                            </Box>
                        </DtCard>
                    </Grid>
                    <Grid item xs={6}>
                        <DtCard header="Add Users with Access" actions={
                            <Button variant="contained" size="small" onClick={handleAddUserDialogOpen}>Add</Button>
                        }>
                            {addUserDialogOpen &&
                                <DtDialog
                                    maxWidth='xs'
                                    actions={
                                        <>
                                            <Button size='small' onClick={handleAddUserDialogClosed}>Cancel</Button>
                                            <Button
                                                size='small'
                                                variant='contained'
                                                type="button"
                                                onClick={() => {
                                                    if (addUserFormRef.current) {
                                                        addUserFormRef.current.submit();
                                                    }
                                                }}
                                            >
                                                Submit
                                            </Button>
                                        </>
                                    }
                                    title="Add New User"
                                    open={addUserDialogOpen}
                                    onClose={handleAddUserDialogClosed}
                                >
                                    <DtForm
                                        ref={addUserFormRef}
                                        fields={[
                                            {
                                                id: "username",
                                                label: "Enter a Username",
                                                type: "text",
                                                required: true,
                                                size: 12,
                                                validate: (value) => {
                                                    if (value && users.includes(value.trim())) {
                                                        return "Username already exists";
                                                    }
                                                    return undefined;
                                                },
                                                showValidationStatusIcon: true,
                                            }
                                        ]}
                                        onSubmit={(data) => {
                                            const errorMessage = handleAddUserValue(data.username);
                                            if (!errorMessage) {
                                                handleAddUserDialogClosed();
                                            }
                                        }}
                                        showReset={false}
                                        showSubmit={false}
                                        gap={2}
                                    />
                                </DtDialog>
                            }

                            {removeUserDialogOpen && (
                                <DtConfirmDialog
                                    maxWidth='xs'
                                    open={removeUserDialogOpen}
                                    onClose={handleRemoveUserDialogClosed}
                                    title="Remove User"
                                    message={
                                        <>Are you sure you want to remove user <b>{selectedUser}</b>?</>
                                    }
                                    confirmColor='primary'
                                    confirmLabel='Remove'
                                    onConfirm={() => handleRemoveUserValue(selectedUser)}
                                />
                            )}

                            <Box>
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: { md: '34vh', lg: '40vh', xl: '54vh' }, maxHeight: { md: '34vh', lg: '40vh', xl: '54vh' }, px: 1 }}>
                                    {users.length > 0 ? (
                                        <DtList
                                            items={users.map(u => ({
                                                id: u,
                                                label: <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{u}</Typography>
                                                </Box>,
                                                actionConfig: {
                                                    mode: "inline",
                                                    actions: [
                                                        {
                                                            type: "delete",
                                                            onClick: () => {
                                                                setSelectedUser(u);
                                                                handleRemoveUserDialogOpen();
                                                            },
                                                            label: "Delete"
                                                        }
                                                    ]
                                                }
                                            }))}
                                            selectable={false}
                                            listItemSx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
                                        />
                                    ) : <EmptyState
                                        subheader='Please add new user using the button above'
                                        icon={<AccountCircle fontSize="large" color="primary" />}
                                        minHeight={200}
                                        header={'No Users Found'} />}
                                </Box>
                            </Box>
                        </DtCard>
                    </Grid>
                </Grid>
                
                {/* Expressions Tab */}
                <Box sx={{ p: 2 }}>
                    <ExpressionsControl
                        expressions={selectedPackage?.Expressions}
                        updateParentExpressions={updateExpressions}
                        title="Any 'False' Expression will suppress/hide control"
                        previewLength={30}
                    />
                </Box>
            </DtTabs>
        </Box>
    );
};

export default PackagesTab;